import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import { CircularProgress, TextField, IconButton } from "@mui/material";
import { useState } from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { AutoToggle } from "@/Chat/ChatSidebar/ChatInputBox/AutoToggle.tsx";

import styles from "./ChatInputBox.module.css";
import { RobotoLLMContextWidget } from "./RobotoLLMContextWidget";

interface ChatInputBoxProps {
  onSubmit: (message: string) => Promise<void>;
  onCancel: () => void | Promise<void>;
  loading: boolean;
  isCancelling?: boolean;
}

/**
 * Pure ChatInput component that accepts callbacks.
 * All data fetching and mutations are handled by the parent Chat component.
 */
export const ChatInputBox = ({
  onSubmit,
  onCancel,
  loading,
  isCancelling = false,
}: ChatInputBoxProps) => {
  const {
    context,
    contextDisplayNames,
    handleContextDelete,
    autoNavEnabled,
    handleToggleAutoNav,
  } = useAIChat();
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (message.trim() && !loading && !isCancelling) {
      void onSubmit(message);
      setMessage("");
    }
  };

  return (
    <div className={styles.centeringContainer}>
      <div className={styles.topContainer}>
        <RobotoLLMContextWidget
          context={context}
          displayNames={contextDisplayNames}
          onDelete={handleContextDelete}
        />

        <div className={styles.textFieldContainer}>
          <TextField
            fullWidth
            multiline
            maxRows={10}
            minRows={3}
            placeholder="Say something to Roboto's AI assistant"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className={styles.inputField}
            inputProps={{
              style: { fontSize: "0.875rem" },
              "aria-label": "Chat message input",
            }}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: "none",
                },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: "none",
                },
              "& .MuiOutlinedInput-root": {
                padding: 0,
              },
            }}
          />
          <div className={styles.controlsRow}>
            <div className={styles.autoNavToggle}>
              <AutoToggle
                enabled={autoNavEnabled}
                onToggle={handleToggleAutoNav}
              />
            </div>
            <div className={styles.sendButtonContainer}>
              {loading && (
                <IconButton
                  size="small"
                  aria-label={isCancelling ? "Cancelling..." : "Cancel message"}
                  onClick={() => void onCancel()}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CancelIcon sx={{ width: 16, height: 16 }} />
                  )}
                </IconButton>
              )}
              {!loading && (
                <IconButton
                  size="small"
                  onClick={handleSend}
                  disabled={!message.trim()}
                  aria-label="Send message"
                >
                  <SendIcon sx={{ width: 16, height: 16 }} />
                </IconButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
